/**
 * IANA-timezone-aware conversion between UTC ISO strings and the
 * wall-clock format datetime-local inputs use.
 *
 * Background: <input type="datetime-local"> has no timezone awareness.
 * It returns "YYYY-MM-DDTHH:mm" as a naive string. If we shove a UTC
 * ISO into it, the operator sees the UTC value labeled with no
 * timezone — confusing when the display row (rendered with tz=ET)
 * shows the same instant 4 hours earlier. These helpers convert in
 * both directions against an IANA timezone name.
 *
 * Used by the Event Details editor in CampaignDetail.tsx. Pulled into
 * a separate module for unit-testability.
 */

const DT_LOCAL_FMT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

/**
 * Render a UTC datetime (ISO string, with or without Z suffix) as the
 * wall-clock value in `tz`, formatted for a datetime-local input.
 *
 * Example: ("2026-10-22T09:30:00", "America/New_York") -> "2026-10-22T05:30"
 * (because 09:30 UTC == 05:30 EDT in October)
 *
 * Returns '' when the input is null/undefined.
 */
export function utcIsoToWallClockInTz(
  isoUtc: string | null | undefined,
  tz: string,
): string {
  if (!isoUtc) return '';
  // Backend stores naive UTC; if the string lacks a Z suffix, treat it
  // as UTC. `new Date(s + 'Z')` parses unambiguously as UTC.
  const normalized = /[Z+-]\d{0,2}:?\d{0,2}$/.test(isoUtc) ? isoUtc : isoUtc + 'Z';
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    ...DT_LOCAL_FMT,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Inverse: convert a "YYYY-MM-DDTHH:mm" wall-clock value that the
 * operator typed (semantically in `tz`) to a UTC ISO string suitable
 * for storage.
 *
 * Example: ("2026-10-22T09:30", "America/New_York") -> "2026-10-22T13:30:00"
 * (because 09:30 EDT == 13:30 UTC)
 *
 * Works through DST transitions because the offset is recomputed at
 * the candidate UTC instant via Intl.DateTimeFormat. A single iterative
 * pass converges since the offset is locally constant either side of
 * any DST boundary.
 *
 * Throws on empty / unparseable input.
 */
export function wallClockInTzToUtcIso(wallClock: string, tz: string): string {
  if (!wallClock) throw new Error('wallClock is required');
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wallClock);
  if (!match) throw new Error(`unparseable wall-clock string: ${wallClock}`);
  const [, y, mo, d, h, mi] = match;

  // First guess: treat the wall-clock as if it were UTC.
  const asUtcMs = Date.UTC(+y, +mo - 1, +d, +h, +mi);

  // What wall-clock does this UTC instant render to in tz?
  const renderInTz = (utcMs: number): number => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      ...DT_LOCAL_FMT,
    }).formatToParts(new Date(utcMs));
    const num = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    return Date.UTC(num('year'), num('month') - 1, num('day'),
                    num('hour'), num('minute'));
  };

  // Adjust by the offset between guess and what the guess renders to.
  // The renderer drops the UTC value into the tz's wall-clock; the
  // offset between that and our naive UTC interpretation IS the tz
  // offset (with a sign flip).
  let utcMs = asUtcMs - (renderInTz(asUtcMs) - asUtcMs);

  // One refinement pass to handle DST boundaries: re-check the
  // rendered wall-clock matches the input. If not (DST shift between
  // the guess and the actual instant), adjust once more.
  if (renderInTz(utcMs) !== asUtcMs) {
    utcMs = asUtcMs - (renderInTz(utcMs) - asUtcMs);
  }

  return new Date(utcMs).toISOString().slice(0, 19);
}
